import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import clsx from 'clsx';
import toast from 'utils/toast.jsx';
import pluralize from 'pluralize';

import { closeModal, openModal } from 'features/utilsSlice';
import { setGig } from 'features/gigsSlice';
import { postRequest } from 'utils/api';
import { getGigOwner, isAutomaticGG } from 'utils/helpers';
import { downloadAllAssets } from 'utils/downloadAssets';
import useModalOpen from 'hooks/useModalOpen';
import useCurrentProfile from 'hooks/useCurrentProfile';
import MessageWindowIcon from 'public/icons/msg-window.svg';
import EditIcon from 'public/icons/edit-grey.svg';
import DocumentIcon from 'public/icons/document.svg';
import MilestoneInfo from './MilestoneInfo';
import ReviewAssetDialog from './ReviewAssetDialog';
import Deliverables from './Deliverables';
import RejectMilestoneDialog from './RejectMilestoneDialog';
import SendDeliverableDialog from './SendDeliverableDialog';
import PaymentChargeBanner from './PaymentChargeBanner';
import TransitionDialog from 'components/TransitionDialog';
import DisplayAsset from 'components/DisplayAsset';
import BottomButtons from 'components/BottomButtons';
import IconMainContainer from 'components/IconMainContainer';
import ConfirmationDialog from 'components/ConfirmationDialog';
import styles from 'styles/components/milestones/Milestone.module.scss';

const useStyles = makeStyles(theme => ({
  paper: {
    height: '90%',
    marginTop: 'auto',

    [theme.breakpoints.up('md')]: {
      height: '100%',
    },
  },
}));

const width = { width: '31%', maxWidth: 180 };
const alertStyleNotActive = { width: 'auto', margin: '20px 15px 0' };
const noPadding = { padding: 0 };
const alignCenter = { alignItems: 'center' };
const marginRight = { marginRight: 10 };

const advertiserLabels = {
  assets_provided: 'Post an existing ad/materials',
  creator_assets: 'Influencer creates content',
};
const influencerLabels = {
  assets_provided: 'The delivery for this ToDo requires reposting the assets that are provided.',
  creator_assets:
    'The delivery for this ToDo requires adapting the provided assets (if provided) and creating the content for the post.',
};

const formatLiveTime = milestone => {
  if (milestone.event_attendance)
    return `${milestone.live_time_duration * milestone.variable_quantity} ${pluralize(
      milestone.live_time_unit,
      milestone.live_time_duration * milestone.variable_quantity
    )}`;

  if (milestone.live_time_unit === 'none') return `1 ${milestone.inventory_space.name}`;

  return `${milestone.live_time_duration} ${pluralize(
    milestone.live_time_unit,
    milestone.live_time_duration
  )}`;
};

const MilestoneDialog = ({ milestone, gig }) => {
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [openReject, setOpenReject] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const classes = useStyles();
  const router = useRouter();
  const open = useModalOpen('Milestone');
  const profile = useCurrentProfile();
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.auth.currentUser);
  const gigOwner = getGigOwner(currentUser, gig.user_id);
  const influencerAssigned =
    gig.profile_id === profile?.id || currentUser?.profile_ids?.includes(gig.profile_id);

  const automaticGG = isAutomaticGG(gig);

  useEffect(() => {
    if (!milestone) return;

    router.prefetch(`/gigs/${milestone.gig_id}?tab=chat`);
  }, [milestone?.gig_id, milestone?.id, milestone, router]);

  const handleMessageClick = () =>
    router.push(`/gigs/${milestone.gig_id}?tab=chat`, undefined, {
      shallow: true,
    });

  const handleClose = () => dispatch(closeModal('Milestone'));

  const handleCopy = text =>
    toast(
      'success',
      `Successfully copied the ${text}! Make sure to come back to the app, and upload a proof of posting once you're done on ${milestone.name}.`,
      {
        icon: <IconMainContainer src="/icons/notifications/thumb-up.svg" alt="Thumb Up" />,
      }
    );

  const handleOpenConfirmation = action => {
    dispatch(openModal({ name: 'Confirmation', data: { action } }));
    setOpenConfirmation(true);
  };

  const handleSubmit = action => {
    setIsLoading(true);

    postRequest({
      endpoint: `milestones/${milestone.id}/${action}`,
      successMessage: `${action === 'approve' ? 'Successfully approved ToDo.' : 'ToDo rejected.'}`,
    })
      .then(response => {
        const milestones = gig.milestones.map(m => (m.id === milestone.id ? response.data : m));
        const allMilestonesApproved = milestones.every(
          milestone => milestone.status === 'approved'
        );

        dispatch(
          setGig({ ...gig, status: allMilestonesApproved ? 'completed' : 'active', milestones })
        );
        dispatch(closeModal('Confirmation'));
        handleClose();
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  const handleClick = () => {
    if (gigOwner) handleClose();

    postRequest({
      endpoint: `milestones/${milestone.id}/deliver`,
    }).then(response => {
      const milestones = gig.milestones.map(m => (m.id === milestone.id ? response.data : m));

      dispatch(setGig({ ...gig, milestones }));
      dispatch(openModal({ name: 'Send Deliverable' }));
    });
  };

  const handleGGDeliver = () => {
    postRequest({
      endpoint: `milestones/${milestone.id}/deliver`,
    }).then(response => {
      const milestones = gig.milestones.map(m => (m.id === milestone.id ? response.data : m));

      dispatch(setGig({ ...gig, milestones }));
      dispatch(openModal({ name: 'Send Deliverable' }));
      setOpenConfirmation(false);
    });
  };

  const renderButtons = () => {
    if (
      !gigOwner &&
      influencerAssigned &&
      ['pending', 'rejected'].includes(milestone.status) &&
      (milestone.delivery_screenshot || milestone.views_screenshot)
    ) {
      return (
        <div className={styles.buttons}>
          <Button onClick={handleClose} variant="contained" color="primary" style={marginRight}>
            Close
          </Button>

          <Button
            onClick={() =>
              gig.guaranteed_gains ? handleOpenConfirmation('approve') : handleClick()
            }
            variant="contained"
            color="primary"
            disabled={!deliverEnabled}
          >
            Deliver
          </Button>
        </div>
      );
    }

    if (gigOwner && milestone.status === 'approve_attempting_payment') {
      return (
        <div className={styles.buttons}>
          <Button onClick={handleClose} variant="contained" color="primary">
            Close
          </Button>
        </div>
      );
    }

    if (gigOwner && milestone.status === 'awaiting_approval') {
      return (
        <div className={styles.buttons} style={noPadding}>
          <BottomButtons
            text="Reject"
            textContained="Approve"
            handleClick={() => handleOpenConfirmation('approve')}
            handleClose={() => {
              dispatch(openModal({ name: 'Reject', data: { milestone, gig } }));
              setOpenReject(true);
            }}
          />
        </div>
      );
    }

    return (
      <div className={styles.buttons}>
        <Button onClick={handleClose} variant="contained" color="primary">
          Close
        </Button>
      </div>
    );
  };

  if (!milestone) return null;

  const guaranteedGainsDeliverCondition = automaticGG
    ? milestone?.guaranteed_gains_current > 0
    : milestone?.initial_screenshot.url && milestone?.delivery_screenshot.url && milestone?.guaranteed_gains_current > 0;

  const regularDeliverCondition =
    (milestone?.inventory_space?.event_attendance && milestone.delivery_screenshot.url) ||
    (milestone?.inventory_space?.has_content && milestone.content.url) ||
    ((milestone?.active || milestone?.status === 'rejected' || profile?.allow_early_delivery) &&
      milestone.initial_screenshot.url &&
      milestone.delivery_screenshot.url &&
      (milestone.inventory_space.has_views ? milestone.views_screenshot.url : true));

  const deliverEnabled = milestone?.guaranteed_gains
    ? guaranteedGainsDeliverCondition
    : regularDeliverCondition;

  return (
    <TransitionDialog
      open={open}
      handleClose={handleClose}
      direction="up"
      paperProps={{ className: classes.paper }}
      noCloseIcon
    >
      <div className={styles.gigContainer}>
        <div className={clsx(styles.header, 'border-bottom')}>
          <div>
            <Image
              src={milestone.network.active_icon_url}
              width={26}
              height={26}
              alt={milestone.name}
              unoptimized
            />

            <h2>{milestone.name}</h2>
          </div>

          <div>
            {gig.status !== 'pending' && (gigOwner || influencerAssigned) && (
              <MessageWindowIcon className="hover-icon" onClick={handleMessageClick} />
            )}

            {gig.status === 'draft' && <EditIcon className={clsx('hover-icon', styles.editIcon)} />}
          </div>
        </div>

        <MilestoneInfo gig={gig} milestone={milestone} gigOwner={gigOwner} />

        {gigOwner && milestone.status === 'approve_attempting_payment' && (
          <PaymentChargeBanner milestone={milestone} gig={gig} />
        )}

        {milestone.status === 'pending' &&
          (milestone.active || profile?.allow_early_delivery) &&
          !gig.funded &&
          !gigOwner &&
          gig.accepted_at && (
            <Alert severity="info" style={alertStyleNotActive}>
              {`This gig is currently not funded. We're waiting for ${gig.user.display_name} to finalize the payment
            (within 72hrs), before you can start working.`}
            </Alert>
          )}

        {milestone.assets_type !== 'no_assets' && (
          <div className={clsx(styles.deliverables, 'border-bottom')}>
            <div className={styles.heading}>
              <h3>{gigOwner ? 'Type detail' : 'Delivery instructions'}</h3>
            </div>

            <div className={styles.caption}>
              <p>
                {gigOwner
                  ? advertiserLabels[milestone.assets_type]
                  : influencerLabels[milestone.assets_type]}
              </p>
            </div>
          </div>
        )}

        {milestone.assets.length > 0 && (
          <div className={clsx(styles.deliverables, 'border-bottom')}>
            <div className={styles.heading}>
              <h3>ToDo assets</h3>
            </div>

            <div className={styles.assetsImages}>
              {milestone.assets.map(asset => (
                <div
                  className={styles.addAsset}
                  onClick={() => {
                    dispatch(openModal({ name: 'Review Asset' }));
                    setSelectedAsset(asset);
                  }}
                  style={width}
                  key={asset.id}
                >
                  <DisplayAsset asset={asset} />
                </div>
              ))}

              {gig.brand_guidelines_url && (
                <a
                  href={gig.brand_guidelines_url}
                  onClick={e => e.stopPropagation()}
                  style={width}
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className={styles.addAsset} style={{ flexDirection: 'column' }}>
                    <DocumentIcon width={46} />

                    <span style={{ color: '#323643' }}>Brand guideline</span>
                    <small style={{ color: '#323643', fontSize: 10 }}>(Opens in a new tab)</small>
                  </div>
                </a>
              )}
            </div>

            <div className={styles.downloadAllButton}>
              <Button
                onClick={() => downloadAllAssets(milestone.assets)}
                variant="outlined"
                color="primary"
              >
                Download all
              </Button>
            </div>
          </div>
        )}

        {milestone.caption && (
          <div className={clsx(styles.deliverables, 'border-bottom')}>
            <div className={styles.heading}>
              <h3>{milestone.inventory_space.caption_name}</h3>
            </div>

            <div className={styles.caption}>
              <p style={marginRight}>{milestone.caption}</p>

              {!gigOwner && (
                <CopyToClipboard text={milestone.caption}>
                  <Button
                    onClick={() => handleCopy(milestone.inventory_space.caption_name)}
                    variant="outlined"
                  >
                    Copy
                  </Button>
                </CopyToClipboard>
              )}
            </div>
          </div>
        )}

        {milestone.special_instructions && (
          <div className={clsx(styles.deliverables, 'border-bottom')}>
            <div className={styles.heading}>
              <h3>Special instructions</h3>
            </div>

            <div className={styles.caption}>
              <p>{milestone.special_instructions}</p>
            </div>
          </div>
        )}

        {milestone.special_instructions_assets.length > 0 && (
          <div className={clsx(styles.deliverables, 'border-bottom')}>
            <div className={styles.heading}>
              <h3>Special instructions assets</h3>
            </div>

            <div className={styles.assetsImages}>
              {milestone.special_instructions_assets.map(asset => (
                <div
                  className={styles.addAsset}
                  onClick={() => {
                    dispatch(openModal({ name: 'Review Asset' }));
                    setSelectedAsset(asset);
                  }}
                  style={width}
                  key={asset.id}
                >
                  <DisplayAsset asset={asset} />
                </div>
              ))}
            </div>
          </div>
        )}

        {milestone.url && (
          <div className={clsx(styles.deliverables, 'border-bottom')}>
            <div className={styles.heading}>
              <h3>URL:</h3>
            </div>

            <div className={styles.caption} style={alignCenter}>
              <span style={{ minWidth: 200, overflowWrap: 'break-word' }}>{milestone.url}</span>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <CopyToClipboard text={milestone.url}>
                  <Button onClick={() => handleCopy('url')} variant="outlined">
                    Copy
                  </Button>
                </CopyToClipboard>

                <a href={milestone.url} target="_blank" rel="noreferrer">
                  <Button variant="outlined">Open</Button>
                </a>
              </div>
            </div>
          </div>
        )}

        <Deliverables gig={gig} milestone={milestone} />
      </div>

      {renderButtons()}

      <ReviewAssetDialog asset={selectedAsset} milestone={milestone} gig={gig} />

      {openConfirmation && (
        <ConfirmationDialog
          title="Are you sure?"
          handleApprove={() =>
            influencerAssigned && gig.guaranteed_gains ? handleGGDeliver() : handleSubmit('approve')
          }
          disabled={isLoading}
          subtitle={
            influencerAssigned && gig.guaranteed_gains
              ? 'This will close the order and send to the advertiser for review.'
              : ''
          }
        />
      )}

      {openReject && <RejectMilestoneDialog />}
      <SendDeliverableDialog />
    </TransitionDialog>
  );
};

MilestoneDialog.propTypes = {
  gig: PropTypes.object.isRequired,
  milestone: PropTypes.object,
};

MilestoneDialog.defaultProps = {
  milestone: null,
};

export default MilestoneDialog;
